// @flow

import { TweenMax, Power2 } from 'gsap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { type StyledComponent } from 'styled-components';

import { __ } from '../../../i18n/l10n';
import { SUBSTEP_UPDATE, OVERRIDE_BACK } from '../../../store/actions';
import type { RootState } from '../../../store/types';
import { media, colors } from '../../../styles/GlobalStyle';
import { isMobile } from '../../../utils/LayoutUtils';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import { Icon } from './Icon';

type ButtonBackRawProps = {
  className?: string,
  arrowOnly?: boolean,
};

const ButtonBackRaw = ({ className, arrowOnly = false }: ButtonBackRawProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const common = useSelector((state: RootState) => state.common);

  const clicButtonBack = () => {
    if (typeof common.prevStep === 'string') {
      if (common.prevStep.search('/landing') !== -1) {
        history.push(common.prevStep);
      } else {
        window.location.href = common.prevStep;
      }
      return;
    }
    if (common.prevStep !== -1 && common.prevStep !== -2) {
      dispatch({
        type: SUBSTEP_UPDATE,
        payload: common.prevStep,
      });
      dispatch({
        type: OVERRIDE_BACK,
        payload: -1,
      });
      return;
    }
    if (common.subStep === 0 || common.prevStep === -2) {
      if (common.step === 1) {
        return;
      }
      history.push(`/step${common.step - 1}`);
    } else {
      dispatch({
        type: SUBSTEP_UPDATE,
        payload: common.subStep - 1,
      });
    }
    if (!isMobile()) {
      return;
    }
    TweenMax.to(window, 0.3, {
      scrollTo: { y: 'header', autoKill: true },
      ease: Power2.easeInOut,
    });
  };

  return (
    <button
      className={`${String(className)} buttonBack`}
      type={'button'}
      onClick={clicButtonBack}
      tabIndex={-1}
    >
      <span>
        <Icon name={'arrow-right'} />
      </span>
      {!arrowOnly && <span>{__('Back')}</span>}
    </button>
  );
};

export const ButtonBack: StyledComponent<ButtonBackRawProps, {}, typeof ButtonBackRaw> = styled(
  ButtonBackRaw,
)`
  font-weight: 900;
  letter-spacing: 0.6px;
  transition: 300ms all ease-out;
  background-color: transparent;
  border: 0;
  color: ${colors.white};
  position: relative;
  overflow: hidden;
  height: 20px;
  cursor: pointer;
  outline: 0;
  text-transform: uppercase;
  user-select: none;

  svg {
    transform: scaleX(-1);
    width: 17px;
    height: 15px;
    margin-top: 1px;
  }

  span:first-child {
    margin-top: 1px;
    padding-right: 10px;
    ${media.greaterThan('desktop')`
      padding-right: 0;
    `}
  }

  span:nth-child(2) {
    padding-left: 5px;
  }

  a {
    text-decoration: none;
  }

  &:before {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    opacity: 0;
    transition: all 0.2s ease-out;
    background-color: #fff;
  }

  &:focus {
    color: ${colors.yellow};
  }

  ${media.greaterThan('desktop')`
    &:focus {
      color: ${colors.white};
    }
  `}

  &:active {
    transform: scale(0.95) !important;
  }
`;

// export default ButtonBack;
